import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ShieldAlert,
  CircleSlash,
  Eye,
  Inbox,
  ListFilter,
  Settings2,
  Siren,
  Timer,
} from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import { useRemembered } from '../../components/listMemory'
import DataTable from '../../components/DataTable'
import SectionNav from '../../components/SectionNav'
import useHashTab from '../../components/useHashTab'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import IconButton from '../../components/IconButton'
import Menu from '../../components/Menu'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import { DeniedState, ErrorState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import IncidentSettingsPanel from './IncidentSettingsPanel'
import { getPage, listReports, setReportsEnabled } from './incidentsService'
import { incidentsStrings as S } from './strings'
import './incidents.css'
import { serverDetail } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

const STATE_TONE = {
  New: 'primary',
  InProgress: 'warning',
  Completed: 'info',
  Closed: 'dark',
}

const ALL_STATES = Object.keys(S.states)

/**
 * The dates arrive pre-formatted — `dd-MM-yyyy hh:mm tt` — and a report nobody has touched
 * carries an EMPTY STRING rather than a date. (`DateUtil.FormatDateTime` turns the stored
 * `DateTime.MinValue` into "".) An em dash says "never" once, instead of leaving a blank cell
 * that reads as a rendering bug.
 */
const asCell = (value) => displayDate(value) || '—'

/**
 * إدارة الحوادث السيبرانية — the legacy `SecurityReportsManagement`.
 *
 * The list is CLIENT-SIDE, and not by preference: `/get-data` takes no paging, no filter and no
 * sort — it returns every report in one response, which is what the legacy did too. The state
 * checkboxes therefore filter in the browser, pre-checked to the first two states.
 */
export default function IncidentsPage() {
  const toast = useToast()

  const [allRows, setAllRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* THE LOAD'S REFUSAL, kept rather than only toasted. A failed load left the table on its
     "لا توجد بلاغات" empty state under a statistics strip reading zero for every figure — so
     a user without SecurityReportsManagers, including one with no roles at all, was told the
     organisation had recorded no security incidents rather than that they may not see them. */
  const [loadError, setLoadError] = useState(null)
  const [featureOn, setFeatureOn] = useState(false)
  /* THE STATES AND THE SEARCH LEAVE THE SCREEN WITH THE PAGE. The address kept `?page=3`
     and these did not, so opening a report and pressing Back landed on page 3 of every
     report, unsearched — the narrowing that found the row done again for the next one. */
  const [visibleStates, setVisibleStates] = useRemembered('incidents:states', S.defaultStates)
  const [query, setQuery] = useRemembered('incidents:query', '')

  const navigate = useNavigate()

  /* A report is its own page — it carries a message, its attachments and a thread, and
     that is not a sheet's worth of screen (#1412). The row's state travels with it: the
     detail payload carries only the localised text, never the enum. */
  const openReport = useCallback(
    (row) => navigate(`/security-reports/${row.id}`, { state: { state: row.state } }),
    [navigate],
  )
  /* The two sections, named in the URL hash like every other tabbed screen. */
  const [tab, setTab] = useHashTab('reports', ['reports', 'settings'])

  const loadRows = useCallback(
    () =>
      listReports()
        .then((rows) => {
          setLoadError(null)
          setAllRows(rows)
          setLoading(false)
        })
        .catch((error) => {
          setLoadError(error)
          setAllRows([])
          setLoading(false)
          toast.error(serverDetail(error?.message) || S.fail.list)
        }),
    [toast],
  )

  useEffect(() => {
    loadRows()
  }, [loadRows])

  /* The switch and the state vocabulary come from two other endpoints; neither should keep the
     table waiting, and neither failing should empty it. */
  useEffect(() => {
    let cancelled = false
    getPage()
      .then((page) => !cancelled && setFeatureOn(page.enabled))
      .catch(() => {})
    /* The state list moved with the details: it is the report page's control that needs
       it, and this screen filters on the states it already knows (`S.states`). */
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * ASKING TO ENABLE CAN STORE `false`.
   *
   * With no categories defined the handler refuses, writes "false", and explains why — so the
   * switch follows the ANSWER rather than the click. Flipping it optimistically would leave a
   * screen saying the feature is on over a database that says it is off.
   */
  /* Shared with every other activation switch — see useFeatureToggle. It owns
     the in-flight guard now, which is what `switching` used to be. */
  const { toggle: toggleFeature, busy: switching } = useFeatureToggle({
    setValue: setFeatureOn,
    save: setReportsEnabled,
  })

  const rows = useMemo(
    () =>
      visibleStates.length === 0
        ? allRows
        : allRows.filter((row) => visibleStates.includes(row.state)),
    [allRows, visibleStates],
  )

  /* One card per state plus a total, counted off the unfiltered set so the figures do not move as
     the list is narrowed. The cards ARE the state filter: picking one narrows to it, picking it
     again (or the total) clears back to everything, and the checkbox menu stays in sync because
     both write the same `visibleStates`. */
  const stats = useMemo(() => {
    const by = { New: 0, InProgress: 0, Completed: 0, Closed: 0 }
    allRows.forEach((row) => {
      if (by[row.state] != null) by[row.state] += 1
    })
    return { total: allRows.length, ...by }
  }, [allRows])

  const soleState = visibleStates.length === 1 ? visibleStates[0] : null

  const pickState = (id) => {
    if (id === 'total') setVisibleStates([])
    else setVisibleStates(soleState === id ? [] : [id])
  }

  const toggleState = (state) =>
    setVisibleStates((previous) =>
      previous.includes(state) ? previous.filter((item) => item !== state) : [...previous, state],
    )

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="inc__index">{index + 1}</span>,
      },
      { key: 'id', header: S.columns.id, width: '96px' },
      {
        key: 'title',
        header: S.columns.title,
        width: '280px',
        render: (row) => (
          <bdi className="inc__title" title={row.title}>
            {row.title}
          </bdi>
        ),
      },
      { key: 'category', header: S.columns.category, render: (row) => <bdi>{row.category}</bdi> },
      {
        key: 'createdOn',
        header: S.columns.createdOn,
        /* Sorted on a timestamp, not on the text. The API sends `dd-MM-yyyy hh:mm tt`, which
           compares by day of month — so without this the column looks sorted and is not. */
        sortValue: (row) => row.createdAt,
        render: (row) => (
          <span className="inc__date" dir="ltr">
            {asCell(row.createdOn)}
          </span>
        ),
      },
      {
        key: 'lastUpdate',
        header: S.columns.lastUpdate,
        sortValue: (row) => row.updatedAt,
        render: (row) => (
          <span className="inc__date" dir="ltr">
            {asCell(row.lastUpdate)}
          </span>
        ),
      },
      { key: 'reporter', header: S.columns.reporter, render: (row) => <bdi>{row.reporter}</bdi> },
      {
        key: 'state',
        header: S.columns.state,
        value: (row) => S.states[row.state] ?? row.state,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.state] ?? 'dark'}>
            {S.states[row.state] ?? row.state}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: <Eye size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        /* The bell that used to sit here has gone. There is no notifications endpoint on this
           slice — not in the API and not in the legacy controller either — so it was a button
           that could never do anything. See COSMETIC_UI.md. */
        render: (row) => (
          <IconButton icon={Eye} label={S.row.view} tone="info" onClick={() => openReport(row)} />
        ),
      },
    ],
    [openReport],
  )

  /* A FAILED LOAD IS NOT AN EMPTY ONE, and the statistics strip below would otherwise print a
     confident zero for every state. The settings tab reaches its own endpoints and shows its
     own states, so only the reports side is answered here. */
  if (loadError && tab === 'reports') {
    return (
      <>
        <PageHeader title={S.title} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState title={S.fail.list} description={loadError.message} />
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={S.title}
        count={tab === 'reports' && !loading ? rows.length : undefined}
      />

      <div className="inc-workspace">
        <SectionNav
          groups={[
            {
              items: [
                { id: 'reports', label: S.sections.reports, icon: Siren },
                { id: 'settings', label: S.sections.settings, icon: Settings2 },
              ],
            },
          ]}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="inc-panel">
          {tab === 'reports' && (
            <>
            {/* No state is chosen on arrival: the page opens on every report, and the cards ARE the
                filter — picking one narrows to it. Whatever is chosen shows as a chip, so which half
                of the reports you are looking at is legible without opening anything. */}
            <StatStrip
              label={S.stats.caption}
              activeId={soleState ?? undefined}
              onSelect={pickState}
              loading={loading}
              items={[
                { id: 'total', icon: Inbox, label: S.stats.total, value: stats.total, tone: 'neutral' },
                { id: 'New', icon: Siren, label: S.states.New, value: stats.New, tone: 'violet' },
                {
                  id: 'InProgress',
                  icon: Timer,
                  label: S.states.InProgress,
                  value: stats.InProgress,
                  tone: 'warning',
                },
                {
                  id: 'Completed',
                  icon: ShieldAlert,
                  label: S.states.Completed,
                  value: stats.Completed,
                  tone: 'info',
                },
                {
                  id: 'Closed',
                  icon: CircleSlash,
                  label: S.states.Closed,
                  value: stats.Closed,
                  tone: 'danger',
                },
              ]}
            />

            <FilterBar
              search={{ value: query, onChange: setQuery, placeholder: S.columns.title }}
              chips={visibleStates.map((state) => ({
                id: state,
                label: S.columns.state,
                value: S.states[state],
                onRemove: () => toggleState(state),
              }))}
              resetLabel={S.cancel}
              onReset={() => setVisibleStates(S.defaultStates)}
            >
              <Menu
                width="280px"
                trigger={({ toggle, open, id }) => (
                  <Button variant="ghost" onClick={toggle} aria-expanded={open} aria-controls={id}>
                    <ListFilter size={16} strokeWidth={1.75} />
                    {S.displayOptions}
                  </Button>
                )}
              >
                {({ close }) => (
                  <div className="inc__states">
                    {ALL_STATES.map((state) => (
                      <Checkbox
                        key={state}
                        id={`state-${state}`}
                        checked={visibleStates.includes(state)}
                        onChange={() => toggleState(state)}
                      >
                        <bdi>{S.states[state]}</bdi>
                      </Checkbox>
                    ))}
                    <div className="inc__states-actions">
                      <Button variant="ghost" onClick={() => setVisibleStates(S.defaultStates)}>
                        {S.cancel}
                      </Button>
                      <Button onClick={close}>{S.apply}</Button>
                    </div>
                  </div>
                )}
              </Menu>
            </FilterBar>

            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              query={query}
              controls={false}
              emptyMessage={S.empty}
              /* The states filter OUTSIDE the table, so it cannot see the row set change: picking
                 one state kept page 3, which the narrowed list no longer has. */
              resetKey={`${visibleStates.join(',')}|${query}`}
              /* The API sorts newest first and the table used to throw that away: with no `initialSort`
                 DataTable falls back to the first sortable column ASCENDING, which put the oldest report
                 at the top of a triage queue and buried today's on the last page. */
              initialSort={{ key: 'createdOn', dir: 'desc' }}
              getRowId={(row) => row.id}
              onRowClick={openReport}
            />
            </>
          )}

          {/* The feature's switch and its categories, on a page of their own rather than a
              switch in the head and a drawer off a button — the two are one decision (the
              feature will not turn on without a category), so they sit together. */}
          {tab === 'settings' && (
            <IncidentSettingsPanel
              enabled={featureOn}
              onToggle={switching ? () => {} : toggleFeature}
              onChanged={loadRows}
            />
          )}
        </div>
      </div>
    </>
  )
}
